import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useLeadReport } from '../../../contexts/LeadReportContext';

const LeadSourceAnalysis = ({ dateRangeState, selectedTeams = [] }) => {
  const theme = useTheme();
  const { fetchSourceFunnel } = useLeadReport();
  const [leadSources, setLeadSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchSourceData = async () => {
      if (!dateRangeState) return;
      
      setLoading(true);
      const { currentStart, currentEnd } = dateRangeState;
      const startDate = currentStart.format('YYYY-MM-DD');
      const endDate = currentEnd.format('YYYY-MM-DD');

      try {
        const data = await fetchSourceFunnel(startDate, endDate, selectedTeams);
        
        if (Array.isArray(data) && data.length > 0) {
          // Aggregate duplicate sources by source_key if any
          const sourceMap = {};
          data.forEach(item => {
            const key = item.source_key || item.source || 'unknown';
            if (sourceMap[key]) {
              // Aggregate if duplicate source_key found
              sourceMap[key].new_leads += item.new_leads || 0;
              sourceMap[key].qualified += item.qualified || 0;
              sourceMap[key].site_visits += item.site_visits || 0;
              sourceMap[key].deals += item.deals || 0;
              // Recalculate conversion percentage
              sourceMap[key].conversion_percentage = sourceMap[key].new_leads > 0
                ? Math.round((sourceMap[key].deals / sourceMap[key].new_leads) * 100 * 10) / 10
                : 0;
            } else {
              sourceMap[key] = { ...item };
            }
          });

          // Transform backend data to frontend format
          const transformedData = Object.values(sourceMap).map(item => ({
            source: item.source || 'Unknown',
            source_key: item.source_key || item.source || 'unknown',
            newLeads: item.new_leads || 0,
            qualified: item.qualified || 0,
            siteVisits: item.site_visits || 0,
            deals: item.deals || 0,
            conversionRate: typeof item.conversion_percentage === 'number'
              ? `${item.conversion_percentage}%`
              : '0%',
            avgDealValue: item.avg_deal_value || 'N/A'
          })).sort((a, b) => b.deals - a.deals); // Sort by deals descending
          
          setLeadSources(transformedData);
        } else {
          setLeadSources([]);
        }
      } catch (error) {
        console.error('Error fetching lead source data:', error);
        setLeadSources([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSourceData();
  }, [dateRangeState, selectedTeams, fetchSourceFunnel]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const displayData = leadSources;
  const MAX_ROWS = 5;
  const hasMoreRows = displayData.length > MAX_ROWS;
  const visibleRows = isExpanded ? displayData : displayData.slice(0, MAX_ROWS);

  // Convert lead sources to CSV format
  const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return '';
    }

    // Define CSV headers
    const headers = [
      'Source',
      'New Leads',
      'Qualified',
      'Site Visits',
      'Deals',
      'Conversion %',
      'Avg Deal Value'
    ];

    // Convert data to CSV rows
    const csvRows = data.map(source => {
      return [
        source.source || '',
        source.newLeads || 0,
        source.qualified || 0,
        source.siteVisits || 0,
        source.deals || 0,
        source.conversionRate || '0%',
        source.avgDealValue || 'N/A'
      ];
    });

    // Combine headers and rows with proper CSV escaping
    const csvContent = [headers, ...csvRows]
      .map(row => 
        row.map(field => {
          const escaped = String(field).replace(/"/g, '""');
          if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
            return `"${escaped}"`;
          }
          return escaped;
        }).join(',')
      )
      .join('\n');

    return csvContent;
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (displayData.length === 0) {
      alert('No data to export');
      return;
    }

    setExporting(true);
    try {
      const csvData = convertToCSV(displayData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = dateRangeState?.currentStart?.format('YYYY-MM-DD') || new Date().toISOString().split('T')[0];
      a.download = `lead_source_analysis_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            fontSize: '1.125rem'
          }}
        >
          Lead Source Analysis
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          size="small"
          onClick={handleExportCSV}
          disabled={exporting || loading || displayData.length === 0}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 1.5,
            fontSize: '0.8125rem',
            py: 0.5,
            px: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            }
          }}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>
      
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: 'background.paper',
          height: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Source
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  New Leads
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Qualified
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Site Visits
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Deals
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Conversion %
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 1.5,
                    px: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Avg Deal Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length > 0 ? (
                visibleRows.map((source, index) => {
                  const conversionRate = parseFloat(source.conversionRate) || 0;
                  const isTopPerformer = index < 2 && source.deals > 0;
                  
                  return (
                    <TableRow 
                      key={source.source_key || index}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          transition: 'background-color 0.2s ease'
                        },
                        ...(isTopPerformer && { 
                          bgcolor: alpha(theme.palette.success.main, 0.04),
                          borderLeft: `3px solid ${theme.palette.success.main}`
                        }),
                        '& td': {
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                          py: 2,
                          px: 2,
                          fontSize: '0.875rem'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              width: 32, 
                              height: 32,
                            }}
                          >
                            <CampaignIcon sx={{ fontSize: '1.125rem' }} />
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight={500}
                              sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}
                            >
                              {source.source}
                            </Typography>
                            {isTopPerformer && (
                              <Chip 
                                label="Top Performer" 
                                size="small" 
                                color="success" 
                                sx={{ 
                                  mt: 0.5,
                                  height: 18,
                                  fontSize: '0.6875rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': {
                                    px: 0.75
                                  }
                                }} 
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                        {source.newLeads.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                        {source.qualified.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                        {source.siteVisits.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                        {source.deals.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={source.conversionRate}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            bgcolor: conversionRate >= 10 
                              ? alpha(theme.palette.success.main, 0.1)
                              : conversionRate > 0 
                                ? alpha(theme.palette.warning.main, 0.1)
                                : alpha(theme.palette.grey[500], 0.1),
                            color: conversionRate >= 10 
                              ? 'success.main' 
                              : conversionRate > 0 
                                ? 'warning.main' 
                                : 'text.secondary',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                        {source.avgDealValue}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={7} 
                    align="center" 
                    sx={{ 
                      py: 6,
                      borderBottom: 'none'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      No lead source data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {hasMoreRows && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 3,
              pt: 2.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}
          >
            <Button
              variant="text"
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.8125rem',
                py: 0.75,
                px: 2,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              {isExpanded ? 'Show Less' : `Show ${displayData.length - MAX_ROWS} More`}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LeadSourceAnalysis;
